import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  Search, Plus, AlertTriangle, User, MapPin, Phone, Mail, Calendar,
  Building, FileText, Clock, ChevronRight, Loader2, Shield, AlertCircle,
  Info, Eye, MessageSquare, Flag
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Disclaimer Component
const DNADisclaimer = () => (
  <Alert className="mb-4 border-amber-200 bg-amber-50">
    <AlertTriangle className="h-4 w-4 text-amber-600" />
    <AlertTitle className="text-amber-800">Important Disclaimer</AlertTitle>
    <AlertDescription className="text-amber-700 text-sm">
      International Pet Registry does not verify, endorse, or take responsibility for the accuracy of 
      information submitted to the Do Not Adopt Registry. All entries are submitted by registered 
      rescue organizations. Before making adoption decisions, we strongly encourage you to:
      <ul className="list-disc list-inside mt-2 space-y-1">
        <li>Verify information through multiple sources</li>
        <li>Conduct your own due diligence and background checks</li>
        <li>Contact the reporting organization for additional details</li>
        <li>Make informed decisions based on your own research</li>
      </ul>
    </AlertDescription>
  </Alert>
);

// Severity Badge Component
const SeverityBadge = ({ severity }) => {
  const colors = {
    low: 'bg-yellow-100 text-yellow-800',
    moderate: 'bg-orange-100 text-orange-800',
    high: 'bg-red-100 text-red-800',
    severe: 'bg-red-600 text-white'
  };
  return (
    <Badge className={colors[severity] || colors.moderate}>
      {severity?.charAt(0).toUpperCase() + severity?.slice(1)}
    </Badge>
  );
};

// Violation Type Badge Component
const ViolationBadge = ({ type }) => {
  const colors = {
    abuse: 'bg-red-100 text-red-800',
    neglect: 'bg-orange-100 text-orange-800',
    fraud: 'bg-purple-100 text-purple-800',
    abandonment: 'bg-blue-100 text-blue-800',
    other: 'bg-stone-100 text-stone-800'
  };
  const labels = {
    abuse: 'Abuse',
    neglect: 'Neglect',
    fraud: 'Fraud',
    abandonment: 'Abandonment',
    other: 'Other'
  };
  return (
    <Badge className={colors[type] || colors.other}>
      {labels[type] || 'Other'}
    </Badge>
  );
};

// Entry Card Component
const DNAEntryCard = ({ entry, onClick }) => (
  <Card 
    className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-red-500"
    onClick={() => onClick(entry)}
  >
    <CardContent className="p-4">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-4 h-4 text-stone-500" />
            <h3 className="font-semibold text-lg">{entry.full_name}</h3>
          </div>
          {entry.aliases && (
            <p className="text-sm text-stone-500 ml-6">AKA: {entry.aliases}</p>
          )}
          <div className="flex items-center gap-4 mt-2 text-sm text-stone-600">
            {entry.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {entry.location}
              </span>
            )}
            {entry.country && (
              <span className="flex items-center gap-1">
                <Flag className="w-3 h-3" />
                {entry.country}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            <ViolationBadge type={entry.violation_type} />
            <SeverityBadge severity={entry.severity} />
          </div>
          {entry.report_count > 0 && (
            <Badge variant="outline" className="text-xs">
              <MessageSquare className="w-3 h-3 mr-1" />
              {entry.report_count} reports
            </Badge>
          )}
        </div>
      </div>
      <p className="text-sm text-stone-600 mt-3 line-clamp-2">{entry.reason}</p>
      <div className="flex items-center justify-between mt-3 text-xs text-stone-500">
        <span className="flex items-center gap-1">
          <Building className="w-3 h-3" />
          {entry.author_name}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true })}
        </span>
      </div>
    </CardContent>
  </Card>
);

// Add Entry Dialog Component
const AddEntryDialog = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    aliases: '',
    location: '',
    city: '',
    state: '',
    country: '',
    email: '',
    phone: '',
    reason: '',
    violation_type: 'other',
    severity: 'moderate',
    date_of_incident: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name.trim() || !formData.reason.trim()) {
      toast.error('Name and reason are required');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post('/shelter/dna/entries', formData);
      toast.success('Entry added to DNA Registry');
      if (response.data.potential_duplicate) {
        toast.warning('Note: A similar entry may already exist');
      }
      onSuccess?.(response.data.entry);
      onClose();
      setFormData({
        full_name: '', aliases: '', location: '', city: '', state: '', country: '',
        email: '', phone: '', reason: '', violation_type: 'other', severity: 'moderate',
        date_of_incident: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add entry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Add to Do Not Adopt Registry
          </DialogTitle>
          <DialogDescription>
            Add a person to the DNA Registry. Please provide accurate and verifiable information.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                placeholder="Enter full name"
                required
              />
            </div>
            <div>
              <Label>Aliases/Other Names</Label>
              <Input
                value={formData.aliases}
                onChange={(e) => setFormData({...formData, aliases: e.target.value})}
                placeholder="Any known aliases"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="Email address"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="Phone number"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>City</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                placeholder="City"
              />
            </div>
            <div>
              <Label>State/Region</Label>
              <Input
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value})}
                placeholder="State/Region"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Input
                value={formData.country}
                onChange={(e) => setFormData({...formData, country: e.target.value})}
                placeholder="Country"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Violation Type</Label>
              <Select 
                value={formData.violation_type} 
                onValueChange={(v) => setFormData({...formData, violation_type: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="abuse">Abuse</SelectItem>
                  <SelectItem value="neglect">Neglect</SelectItem>
                  <SelectItem value="fraud">Fraud</SelectItem>
                  <SelectItem value="abandonment">Abandonment</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Severity</Label>
              <Select 
                value={formData.severity} 
                onValueChange={(v) => setFormData({...formData, severity: v})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="severe">Severe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date of Incident</Label>
              <Input
                type="date"
                value={formData.date_of_incident}
                onChange={(e) => setFormData({...formData, date_of_incident: e.target.value})}
              />
            </div>
          </div>

          <div>
            <Label>Reason / Description *</Label>
            <Textarea
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              placeholder="Provide detailed information about why this person is being added to the DNA registry..."
              rows={4}
              required
            />
          </div>

          <Alert className="border-blue-200 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700 text-sm">
              By submitting this entry, you confirm that the information provided is accurate to the 
              best of your knowledge and that you have a legitimate reason for adding this person 
              to the registry.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : 'Add to Registry'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// View Entry Dialog Component
const ViewEntryDialog = ({ entry, open, onClose, onReportAdded }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddReport, setShowAddReport] = useState(false);
  const [reportForm, setReportForm] = useState({
    report: '',
    incident_type: 'other',
    incident_date: '',
    outcome: ''
  });
  const [submittingReport, setSubmittingReport] = useState(false);

  useEffect(() => {
    if (open && entry) {
      fetchEntryDetails();
    }
  }, [open, entry]);

  const fetchEntryDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/shelter/dna/entries/${entry.entry_id}`);
      setReports(response.data.reports || []);
    } catch (error) {
      toast.error('Failed to load entry details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddReport = async (e) => {
    e.preventDefault();
    if (!reportForm.report.trim()) {
      toast.error('Report text is required');
      return;
    }

    setSubmittingReport(true);
    try {
      await axios.post(`/shelter/dna/entries/${entry.entry_id}/reports`, reportForm);
      toast.success('Report added successfully');
      setShowAddReport(false);
      setReportForm({ report: '', incident_type: 'other', incident_date: '', outcome: '' });
      fetchEntryDetails();
      onReportAdded?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add report');
    } finally {
      setSubmittingReport(false);
    }
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-500" />
            DNA Registry Entry
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Person Details */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {entry.full_name}
                  </CardTitle>
                  {entry.aliases && (
                    <CardDescription>Also known as: {entry.aliases}</CardDescription>
                  )}
                </div>
                <div className="flex gap-2">
                  <ViolationBadge type={entry.violation_type} />
                  <SeverityBadge severity={entry.severity} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {(entry.city || entry.state || entry.country) && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-stone-400" />
                    <span>
                      {[entry.city, entry.state, entry.country].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                {entry.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-stone-400" />
                    <span>{entry.email}</span>
                  </div>
                )}
                {entry.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-stone-400" />
                    <span>{entry.phone}</span>
                  </div>
                )}
                {entry.date_of_incident && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-stone-400" />
                    <span>Incident Date: {entry.date_of_incident}</span>
                  </div>
                )}
              </div>

              <div className="border-t pt-3">
                <Label className="text-stone-500 text-xs">Reason for Listing</Label>
                <p className="mt-1">{entry.reason}</p>
              </div>

              <div className="flex items-center justify-between text-xs text-stone-500 border-t pt-3">
                <span className="flex items-center gap-1">
                  <Building className="w-3 h-3" />
                  Added by: {entry.author_name}
                  {entry.author_location && ` (${entry.author_location})`}
                </span>
                <span>{new Date(entry.created_at).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>

          {/* Reports Section */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Additional Reports ({reports.length})
                </CardTitle>
                <Button size="sm" onClick={() => setShowAddReport(!showAddReport)}>
                  <Plus className="w-4 h-4 mr-1" /> Add Report
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {showAddReport && (
                <form onSubmit={handleAddReport} className="mb-4 p-3 bg-stone-50 rounded-lg space-y-3">
                  <div className="grid md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Incident Type</Label>
                      <Select 
                        value={reportForm.incident_type}
                        onValueChange={(v) => setReportForm({...reportForm, incident_type: v})}
                      >
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adoption_attempt">Adoption Attempt</SelectItem>
                          <SelectItem value="abuse_report">Abuse Report</SelectItem>
                          <SelectItem value="fraud">Fraud</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Incident Date</Label>
                      <Input
                        type="date"
                        className="h-8"
                        value={reportForm.incident_date}
                        onChange={(e) => setReportForm({...reportForm, incident_date: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Outcome</Label>
                      <Input
                        className="h-8"
                        value={reportForm.outcome}
                        onChange={(e) => setReportForm({...reportForm, outcome: e.target.value})}
                        placeholder="e.g., Adoption denied"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Report Details *</Label>
                    <Textarea
                      value={reportForm.report}
                      onChange={(e) => setReportForm({...reportForm, report: e.target.value})}
                      placeholder="Describe the incident or additional information..."
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowAddReport(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={submittingReport}>
                      {submittingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Report'}
                    </Button>
                  </div>
                </form>
              )}

              {loading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
              ) : reports.length === 0 ? (
                <p className="text-center text-stone-500 py-4">No additional reports yet</p>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-3">
                    {reports.map((report) => (
                      <div key={report.report_id} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {report.incident_type?.replace('_', ' ')}
                            </Badge>
                            {report.outcome && (
                              <Badge variant="secondary" className="text-xs">
                                {report.outcome}
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-stone-500">
                            {report.incident_date || new Date(report.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm">{report.report}</p>
                        <div className="flex items-center gap-1 mt-2 text-xs text-stone-500">
                          <Building className="w-3 h-3" />
                          {report.author_name}
                          {report.author_location && ` • ${report.author_location}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main DNA Registry Component
const DNARegistry = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [activeTab, setActiveTab] = useState('search');

  useEffect(() => {
    fetchEntries();
    fetchStats();
  }, []);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/shelter/dna/entries');
      setEntries(response.data.entries || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Premium subscription required to access DNA Registry');
      } else {
        toast.error('Failed to load DNA Registry');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/shelter/dna/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim() || searchQuery.length < 2) {
      toast.error('Please enter at least 2 characters to search');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`/shelter/dna/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
      if (response.data.count === 0) {
        toast.info('No matches found in DNA Registry');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  return (
    <div className="space-y-4">
      <DNADisclaimer />

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3">
            <p className="text-xs text-stone-500">Total Entries</p>
            <p className="text-2xl font-bold text-red-600">{stats.total_entries}</p>
          </Card>
          <Card className="p-3">
            <p className="text-xs text-stone-500">Total Reports</p>
            <p className="text-2xl font-bold">{stats.total_reports}</p>
          </Card>
          <Card className="p-3">
            <p className="text-xs text-stone-500">Your Contributions</p>
            <p className="text-2xl font-bold text-blue-600">{stats.user_contributions?.entries || 0}</p>
          </Card>
          <Card className="p-3">
            <p className="text-xs text-stone-500">Recent (30 days)</p>
            <p className="text-2xl font-bold text-amber-600">{stats.recent_entries}</p>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="search">
              <Search className="w-4 h-4 mr-1" /> Search
            </TabsTrigger>
            <TabsTrigger value="browse">
              <Eye className="w-4 h-4 mr-1" /> Browse All
            </TabsTrigger>
          </TabsList>
          <Button onClick={() => setShowAddDialog(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-1" /> Add Entry
          </Button>
        </div>

        <TabsContent value="search" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="w-5 h-5" />
                Search DNA Registry
              </CardTitle>
              <CardDescription>
                Search by name, email, phone, or aliases before approving any adoption
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex gap-2">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter name, email, or phone number..."
                  className="flex-1"
                />
                <Button type="submit" disabled={searching}>
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
                {searchResults && (
                  <Button type="button" variant="outline" onClick={clearSearch}>
                    Clear
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Search Results */}
          {searchResults && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  Search Results: {searchResults.count} match{searchResults.count !== 1 ? 'es' : ''}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {searchResults.results.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="w-12 h-12 mx-auto text-green-500 mb-2" />
                    <p className="text-green-700 font-medium">No matches found</p>
                    <p className="text-sm text-stone-500">This person is not in the DNA Registry</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {searchResults.results.map((entry) => (
                      <DNAEntryCard
                        key={entry.entry_id}
                        entry={entry}
                        onClick={setSelectedEntry}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="browse" className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
            </div>
          ) : entries.length === 0 ? (
            <Card className="p-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-stone-400 mb-2" />
              <p className="text-stone-600">No entries in the DNA Registry yet</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {entries.map((entry) => (
                <DNAEntryCard
                  key={entry.entry_id}
                  entry={entry}
                  onClick={setSelectedEntry}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Entry Dialog */}
      <AddEntryDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSuccess={() => {
          fetchEntries();
          fetchStats();
        }}
      />

      {/* View Entry Dialog */}
      <ViewEntryDialog
        entry={selectedEntry}
        open={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        onReportAdded={() => {
          fetchEntries();
          fetchStats();
        }}
      />
    </div>
  );
};

export default DNARegistry;
